import {
  GearIcon,
  MediaDeviceMenu,
  StartMediaButton,
  TrackToggle,
  useDisconnectButton,
  useLayoutContext,
  useLocalParticipantPermissions,
  usePersistentUserChoices,
} from "@livekit/components-react";
import { supportsScreenSharing } from "@livekit/components-core";
import clsx from "clsx";
import { Track } from "livekit-client";
import {
  FileText,
  Library,
  Maximize2,
  MessageSquare,
  MoreVertical,
  PhoneOff,
  Settings,
  UserPlus,
  Users,
} from "lucide-react";
import * as React from "react";
import { persistAudioOutputDeviceId } from "../utils/liveInterviewAudioOutput";
import { LiveInterviewAudioDeviceMenu } from "./LiveInterviewAudioDeviceMenu";
import { LiveInterviewReactionsPicker } from "./LiveInterviewReactionsPicker";

/** Avoid recursive "hall of mirrors" when sharing the meeting tab (Chrome tab picker). */
const SCREEN_SHARE_CAPTURE_OPTIONS = {
  audio: true,
  selfBrowserSurface: "exclude",
  preferCurrentTab: false,
};

const trackSourceToProtocol = (source) => {
  switch (source) {
    case Track.Source.Camera:
      return 1;
    case Track.Source.Microphone:
      return 2;
    case Track.Source.ScreenShare:
      return 3;
    default:
      return 0;
  }
};

/** Same behavior as LiveKit SettingsMenuToggle (not exported from package root in some builds). */
function LiveInterviewSettingsToggle({ showIcon, showText }) {
  const { widget } = useLayoutContext();
  return (
    <button
      type="button"
      className="lk-button lk-settings-toggle"
      aria-pressed={widget.state?.showSettings ? "true" : "false"}
      onClick={() => widget.dispatch?.({ msg: "toggle_settings" })}
    >
      {showIcon && <GearIcon />}
      {showText && "Settings"}
    </button>
  );
}

/** Overflow menu on the meeting control bar (PiP / stop video / layout options removed). */
function MeetingBottomMoreMenu({
  activePanel,
  onOpenPanel,
  transcriptPanelAvailable,
  canManageRecording,
  canInviteParticipants,
  pendingJoinRequestsCount = 0,
  chatUnreadCount = 0,
  recordingLive = false,
  isFullscreen = false,
  onToggleFullscreen,
  onOpenSettings,
}) {
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (panel) => {
    if (panel === activePanel) {
      onOpenPanel?.("");
    } else {
      onOpenPanel?.(panel);
    }
    setOpen(false);
  };

  const items = [];
  if (transcriptPanelAvailable) {
    items.push({
      key: "meeting-notes",
      icon: FileText,
      label: "Meeting Notes",
      active: activePanel === "transcript",
      onClick: () => toggle("transcript"),
    });
  }
  items.push({
    key: "fullscreen",
    icon: Maximize2,
    label: isFullscreen ? "Exit Full Screen" : "Enter Full Screen",
    onClick: () => {
      onToggleFullscreen?.();
      setOpen(false);
    },
  });
  if (canInviteParticipants) {
    items.push({
      key: "invite-participants",
      icon: UserPlus,
      label: "Invite Participants",
      badge: pendingJoinRequestsCount > 0 ? pendingJoinRequestsCount : null,
      active: activePanel === "participants",
      onClick: () => toggle("participants"),
    });
  }
  if (canManageRecording) {
    items.push({
      key: "recordings",
      icon: Library,
      label: "Recording",
      active: activePanel === "recordings" || recordingLive,
      onClick: () => toggle("recordings"),
    });
  }
  items.push({
    key: "people",
    icon: Users,
    label: "People",
    active: activePanel === "participants",
    onClick: () => toggle("participants"),
  });
  items.push({
    key: "settings",
    icon: Settings,
    label: "Settings",
    onClick: () => {
      onOpenSettings?.();
      setOpen(false);
    },
  });

  if (!items.length) return null;

  return (
    <div className="li-meet-bottom-more-wrap" ref={menuRef} data-tooltip="More options">
      <button
        type="button"
        className="li-meet-bottom-more-btn"
        aria-label="More meeting options"
        title="More options"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <MoreVertical size={20} aria-hidden />
      </button>
      {open ? (
        <div className="li-meet-bottom-more-menu" role="menu">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={clsx("li-meet-bottom-more-item", item.active && "is-active")}
              disabled={item.disabled}
              onClick={item.onClick}
            >
              <item.icon size={18} aria-hidden />
              <span>{item.label}</span>
              {item.badge != null ? (
                <span className="li-meet-bottom-more-badge">{item.badge}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function LiveInterviewControlBar({
  variation: _variation,
  controls,
  saveUserChoices = true,
  onDeviceError,
  onLeave,
  leaveDisabled = false,
  leaveLabel = "Leave or end meeting",
  meetingMenu,
  ...props
}) {
  const visibleControls = { leave: true, ...controls };
  const { widget } = useLayoutContext();

  const localPermissions = useLocalParticipantPermissions();

  if (!localPermissions) {
    visibleControls.camera = false;
    visibleControls.chat = false;
    visibleControls.microphone = false;
    visibleControls.screenShare = false;
  } else {
    const canPublishSource = (source) =>
      localPermissions.canPublish &&
      (localPermissions.canPublishSources.length === 0 ||
        localPermissions.canPublishSources.includes(trackSourceToProtocol(source)));
    visibleControls.camera ??= canPublishSource(Track.Source.Camera);
    visibleControls.microphone ??= canPublishSource(Track.Source.Microphone);
    visibleControls.screenShare ??= canPublishSource(Track.Source.ScreenShare);
    visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
  }

  const browserSupportsScreenSharing = supportsScreenSharing();

  const [isScreenShareEnabled, setIsScreenShareEnabled] = React.useState(false);
  const previousScreenShareEnabledRef = React.useRef(false);

  const onScreenShareChange = React.useCallback((enabled) => {
    setIsScreenShareEnabled(enabled);
  }, []);

  React.useEffect(() => {
    const wasEnabled = previousScreenShareEnabledRef.current;
    previousScreenShareEnabledRef.current = isScreenShareEnabled;
    if (wasEnabled && !isScreenShareEnabled) {
      // When browser focus stays on the shared tab/window after stopping share,
      // guide focus back to the meeting tab where possible.
      window.setTimeout(() => {
        try {
          window.focus();
        } catch {
          // Ignore browser-level focus restrictions.
        }
      }, 120);
    }
  }, [isScreenShareEnabled]);

  const htmlProps = {
    ...props,
    className: clsx("lk-control-bar", props.className),
  };

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: !saveUserChoices });

  const meetingDurationLabel = String(meetingMenu?.meetingDurationLabel || "").trim();
  const chatUnreadCount = Number(meetingMenu?.chatUnreadCount || 0);
  const chatPanelOpen = meetingMenu?.activePanel === "chat";

  const toggleChatPanel = React.useCallback(() => {
    if (!meetingMenu?.onOpenPanel) return;
    meetingMenu.onOpenPanel(chatPanelOpen ? "" : "chat");
  }, [chatPanelOpen, meetingMenu]);

  const microphoneOnChange = React.useCallback(
    (enabled, isUserInitiated) => (isUserInitiated ? saveAudioInputEnabled(enabled) : null),
    [saveAudioInputEnabled],
  );

  const cameraOnChange = React.useCallback(
    (enabled, isUserInitiated) => (isUserInitiated ? saveVideoInputEnabled(enabled) : null),
    [saveVideoInputEnabled],
  );

  const { buttonProps: disconnectProps } = useDisconnectButton({});
  const leaveButtonProps = React.useMemo(() => {
    const { onClick: disconnectClick, ...rest } = disconnectProps;
    return {
      ...rest,
      type: "button",
      onClick: async (event) => {
        try {
          const shouldDisconnect = await onLeave?.(event);
          if (shouldDisconnect === false) {
            return;
          }
        } catch (error) {
          console.warn("[LIVEKIT_LEAVE] pre-disconnect handler failed", error);
        }
        disconnectClick?.(event);
      },
    };
  }, [disconnectProps, onLeave]);

  return (
    <div {...htmlProps}>
      {meetingDurationLabel ? (
        <>
          <div
            className="li-control-bar-meeting"
            aria-label={`Meeting duration ${meetingDurationLabel}`}
          >
            <span className="li-control-bar-meeting-time">{meetingDurationLabel}</span>
          </div>
          <span className="li-control-bar-rec-divider" aria-hidden />
        </>
      ) : null}
      {visibleControls.microphone && (
        <div className="lk-button-group" data-tooltip="Microphone">
          <TrackToggle
            className="li-control-icon"
            source={Track.Source.Microphone}
            showIcon
            title="Microphone"
            aria-label="Microphone"
            onChange={microphoneOnChange}
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Microphone, error })}
          />
          <div className="lk-button-group-menu">
            <LiveInterviewAudioDeviceMenu
              onMicrophoneDeviceChange={(deviceId) =>
                saveAudioInputDeviceId(deviceId ?? "default")
              }
              onSpeakerDeviceChange={(deviceId) => {
                persistAudioOutputDeviceId(deviceId ?? "");
              }}
            />
          </div>
        </div>
      )}
      {visibleControls.camera && (
        <div className="lk-button-group" data-tooltip="Camera">
          <TrackToggle
            className="li-control-icon"
            source={Track.Source.Camera}
            showIcon
            title="Camera"
            aria-label="Camera"
            onChange={cameraOnChange}
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Camera, error })}
          />
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="videoinput"
              title="Camera devices"
              aria-label="Camera devices"
              onActiveDeviceChange={(_kind, deviceId) =>
                saveVideoInputDeviceId(deviceId ?? "default")
              }
            />
          </div>
        </div>
      )}
      {visibleControls.screenShare && browserSupportsScreenSharing && (
        <div data-tooltip={isScreenShareEnabled ? "Stop share" : "Share screen"}>
          <TrackToggle
            className="li-control-icon"
            source={Track.Source.ScreenShare}
            captureOptions={SCREEN_SHARE_CAPTURE_OPTIONS}
            showIcon
            title={isScreenShareEnabled ? "Stop share" : "Share screen"}
            aria-label={isScreenShareEnabled ? "Stop share" : "Share screen"}
            onChange={onScreenShareChange}
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.ScreenShare, error })}
          />
        </div>
      )}
      {meetingMenu ? (
        <>
          <LiveInterviewReactionsPicker />
          <button
            type="button"
            className={clsx(
              "li-meet-bottom-more-btn",
              "li-meet-bottom-chat-btn",
              chatPanelOpen && "is-active",
            )}
            title="Chat"
            aria-label={
              chatUnreadCount > 0 ? `Chat, ${chatUnreadCount} unread messages` : "Chat"
            }
            aria-pressed={chatPanelOpen ? "true" : "false"}
            data-tooltip="Chat"
            onClick={toggleChatPanel}
          >
            <MessageSquare size={18} aria-hidden />
            {chatUnreadCount > 0 ? (
              <span className="li-meet-bottom-chat-badge" aria-hidden>
                {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
              </span>
            ) : null}
          </button>
          <MeetingBottomMoreMenu
            {...meetingMenu}
            onOpenSettings={() => widget.dispatch?.({ msg: "toggle_settings" })}
          />
        </>
      ) : null}
      {visibleControls.settings && !meetingMenu && (
        <LiveInterviewSettingsToggle showIcon showText={false} />
      )}
      {visibleControls.leave && (
        <button
          {...leaveButtonProps}
          type="button"
          disabled={leaveDisabled || leaveButtonProps.disabled}
          title={leaveLabel}
          aria-label={leaveLabel}
          data-tooltip={leaveLabel}
          className={clsx(
            "lk-button",
            "li-control-icon",
            "live-interview-leave-btn",
            "li-meet-leave-btn",
            leaveButtonProps.className,
          )}
        >
          <PhoneOff size={18} aria-hidden />
        </button>
      )}
      <StartMediaButton />
    </div>
  );
}
